import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { CacheFactory } from './CacheFactory'
import { Change } from './Change'
import { Charge } from './Charge'
import { Conference } from './Conference'
import { Estimate } from './Estimate'
import { Gift } from './Gift'
import { Image } from './Image'
import { Invoice } from './Invoice'
import { Lesson } from './Lesson'
import { Order } from './Order'
import { PSRecord } from './PSRecord'
import { Package } from './Package'
import { Payment } from './Payment'
import { RecordDataMap } from './RecordDataMap'
import { Review } from './Review'
import { Royalty } from './Royalty'
import { FKType, SQLBuilder } from './SQLBuilder'
import { SQLiteFactory } from './SQLiteFactory'
import { Signature } from './Signature'
import { Subaccount } from './Subaccount'
import { Subscription } from './Subscription'
import { Task } from './Task'
import { Team } from './Team'
import { Template } from './Template'
import { Transaction } from './Transaction'
import { User } from './User'

type MessageConstructorArgs = ConstructorArgs<() => Message>

export class Message extends BaseRecord {
    readonly recordType = 'message'

    static MAX_UNREAD_COUNT = 99 // Integer
    type?: string
    subtype?: string
    key?: string
    subaccount?: Subaccount // not stored

    source?: string
    channel?: string
    address?: string

    image?: Image
    message?: string
    viewedDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    longitude?: number
    latitude?: number
    private _thread?: string /* calculated client side (optimized) */
    lastThreadUnreadDate?: moment.Moment
    rcvdDate?: moment.Moment
    threadLastModifiedDate?: moment.Moment

    constructor(data?: Partial<MessageConstructorArgs>, params?: { record?: BaseRecord }) {
        super(data)
        if (!data) {
            this.owner = this.sender = User.initWithId(PSApplication.inbox())
            if (params?.record) {
                this.type = params.record.recordType.replace('user', 'person')
                if (this.type === 'invoice') this.invoice = params.record as Invoice
                else if (this.type === 'payment') this.payment = params.record as Payment
                else if (this.type === 'charge') this.charge = params.record as Charge
                else if (this.type === 'booking') this.booking = params.record as Booking
                else if (this.type === 'team') this.team = params.record as Team
                else if (this.type === 'task') this.task = params.record as Task
                else if (this.type === 'person') this.person = params.record as User
                else if (this.type === 'estimate') this.estimate = params.record as Estimate
                else if (this.type === 'order') this.order = params.record as Order
                else if (this.type === 'package') this.aPackage = params.record as Package
                else if (this.type === 'gift') this.gift = params.record as Gift
                else if (this.type === 'subscription')
                    this.subscription = params.record as Subscription
                else if (this.type === 'conference') this.conference = params.record as Conference
                else if (this.type === 'signature') this.signature = params.record as Signature
                else if (this.type === 'record') this.record = params.record as PSRecord
                else if (this.type === 'review') this.review = params.record as Review
            }
            return
        }

        this.type = data.type ?? undefined
        this.subtype = data.subtype ?? undefined
        this.key = data.key ?? undefined
        this.source = data.source ?? undefined
        this.channel = data.channel ?? undefined
        this.address = data.address ?? undefined
        this.image = this.propertyFromImageArg(data.image, data.image_uri)
        this.message = data.message ?? undefined
        this.viewedDate = this.propertyFromTimestampArg(data.viewed_date)
        this.rcvdDate = this.propertyFromTimestampArg(data.rcvd_date)
        this.threadLastModifiedDate = this.propertyFromTimestampArg(data.thread_last_modified_date)
        this._thread = data.thread ?? undefined
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
        this.longitude = this.propertyFromNumberArg(data.longitude)
        this.latitude = this.propertyFromNumberArg(data.latitude)
    }

    clone(overrides?: Partial<MessageConstructorArgs>): Message {
        return new Message({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Message {
        const message = CacheFactory.getInstance().getRecord(Message, id) ?? new Message({ id })
        return message
    }

    static initWithRecord(record: BaseRecord): Message {
        return new Message(undefined, { record })
    }

    get thread(): string | undefined {
        if (this._thread !== undefined) return this._thread
        return this.type === 'team' ? this.team?.id : this.person?.id
    }

    get instance(): BaseRecord | undefined {
        return (
            this.booking ??
            this.invoice ??
            this.payment ??
            this.charge ??
            this.record ??
            this.gift ??
            this.change ??
            this.signature ??
            this.subscription ??
            this.review ??
            this.lesson ??
            this.aPackage ??
            this.task ??
            this.estimate ??
            this.order ??
            this.royalty ??
            this.conference ??
            this.person ??
            this.team
        )
    }

    get transaction(): Transaction | undefined {
        // @ts-ignore
        return (
            this.booking ??
            this.invoice ??
            this.payment ??
            this.charge ??
            this.order ??
            this.gift ??
            this.subscription ??
            this.aPackage ??
            this.estimate ??
            this.royalty
        )
    }

    set thread(thread: string | undefined) {
        this._thread = thread
    }

    reply(): Message {
        const reply = new Message()
        reply.person = this.person
        if (reply.person) reply.recipient = reply.person
        reply.team = this.team
        reply.thread = this.thread
        return reply
    }

    static async lastSyncDate(threadId: string): Promise<moment.Moment | undefined> {
        const sql = new SQLBuilder()
        sql.addField('lastSyncDate')
        sql.addTable('threads')
        sql.filterBy('id=?', threadId)
        sql.filterBy("createdDate > datetime('now', '-5 minutes')")
        const resultSet = await SQLiteFactory.getInstance().executeSingleRowQuery(sql)
        return resultSet
            ? new RecordDataMap(resultSet).valueForTimeStampKey('lastSyncDate')
            : undefined
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        if (this.type) dict['type'] = this.type
        if (this.subtype) dict['subtype'] = this.subtype
        dict['owner'] = this.owner?.id ?? ''
        dict['sender'] = this.sender?.id ?? ''
        if (this.recipient) dict['recipient'] = this.recipient.id
        if (this.subaccount) dict['subaccount'] = this.subaccount.id
        if (this.team) dict['team'] = this.team.id
        if (this.message) dict['message'] = this.message
        if (this.task) dict['task'] = this.task.id
        if (this.invoice) dict['invoice'] = this.invoice.id
        if (this.estimate) dict['estimate'] = this.estimate.id
        if (this.royalty) dict['royalty'] = this.royalty.id
        if (this.order) dict['order'] = this.order.id
        if (this.review) dict['review'] = this.review.id
        if (this.change) dict['change'] = this.change.id
        if (this.aPackage) dict['package'] = this.aPackage.id
        if (this.gift) dict['gift'] = this.gift.id
        if (this.booking) dict['booking'] = this.booking.id
        if (this.payment) dict['payment'] = this.payment.id
        if (this.charge) dict['charge'] = this.charge.id
        if (this.contact) dict['contact'] = this.contact.id
        if (this.template) dict['template'] = this.template.id
        if (this.latitude) dict['latitude'] = this.latitude
        if (this.longitude) dict['longitude'] = this.longitude
        return dict
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.sender) this.sender = BaseRecord.fromSerializer(data.sender, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.recipient) this.recipient = BaseRecord.fromSerializer(data.recipient, User)
        if (data.subaccount)
            this.subaccount = BaseRecord.fromSerializer(data.subaccount, Subaccount)
        if (data.image) this.image = BaseRecord.fromSerializer(data.image, Image)
        if (data.viewedDate) this.viewedDate = moment(data.viewedDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.invoice) this.invoice = BaseRecord.fromSerializer(data.invoice, Invoice)
        if (data.estimate) this.estimate = BaseRecord.fromSerializer(data.estimate, Estimate)
        if (data.royalty) this.royalty = BaseRecord.fromSerializer(data.royalty, Royalty)
        if (data.order) this.order = BaseRecord.fromSerializer(data.order, Order)
        if (data.aPackage) this.aPackage = BaseRecord.fromSerializer(data.aPackage, Package)
        if (data.payment) this.payment = BaseRecord.fromSerializer(data.payment, Payment)
        if (data.charge) this.charge = BaseRecord.fromSerializer(data.charge, Charge)
        if (data.gift) this.gift = BaseRecord.fromSerializer(data.gift, Gift)
        if (data.task) this.task = BaseRecord.fromSerializer(data.task, Task)
        if (data.team) this.team = BaseRecord.fromSerializer(data.team, Team)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.change) this.change = BaseRecord.fromSerializer(data.change, Change)
        if (data.record) this.record = BaseRecord.fromSerializer(data.record, PSRecord)
        if (data.lesson) this.lesson = BaseRecord.fromSerializer(data.lesson, Lesson)
        if (data.signature) this.signature = BaseRecord.fromSerializer(data.signature, Signature)
        if (data.conference)
            this.conference = BaseRecord.fromSerializer(data.conference, Conference)
        if (data.subscription)
            this.subscription = BaseRecord.fromSerializer(data.subscription, Subscription)
        if (data.review) this.review = BaseRecord.fromSerializer(data.review, Review)
        if (data.contact) this.contact = BaseRecord.fromSerializer(data.contact, User)
        if (data.template) this.template = BaseRecord.fromSerializer(data.template, Template)
        if (data.lastThreadUnreadDate) this.lastThreadUnreadDate = moment(data.lastThreadUnreadDate)
        if (data.rcvdDate) this.rcvdDate = moment(data.rcvdDate)
        if (data.threadLastModifiedDate)
            this.threadLastModifiedDate = moment(data.threadLastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'sender', 'creator', 'recipient', 'person', 'contact'],
        subaccounts: ['subaccount'],
        images: ['image'],
        bookings: ['booking'],
        invoices: ['invoice'],
        estimates: ['estimate'],
        orders: ['order'],
        royalties: ['royalty'],
        packages: ['aPackage'],
        payments: ['payment'],
        charges: ['charge'],
        gifts: ['gift'],
        tasks: ['task'],
        teams: ['team'],
        changes: ['change'],
        records: ['record'],
        lessons: ['lesson'],
        signatures: ['signature'],
        conferences: ['conference'],
        subscriptions: ['subscription'],
        reviews: ['review'],
        templates: ['template'],
    }

    static dataType(): DataType {
        return 'messages'
    }

    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get sender(): User | undefined {
        return this.getForeignRecord('sender', User)
    }
    set sender(sender: User | undefined) {
        this.setForeignRecord('sender', sender)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get recipient(): User | undefined {
        return this.getForeignRecord('recipient', User)
    }
    set recipient(recipient: User | undefined) {
        this.setForeignRecord('recipient', recipient)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get invoice(): Invoice | undefined {
        return this.getForeignRecord('invoice', Invoice)
    }
    set invoice(invoice: Invoice | undefined) {
        this.setForeignRecord('invoice', invoice)
    }
    get estimate(): Estimate | undefined {
        return this.getForeignRecord('estimate', Estimate)
    }
    set estimate(estimate: Estimate | undefined) {
        this.setForeignRecord('estimate', estimate)
    }
    get royalty(): Royalty | undefined {
        return this.getForeignRecord('royalty', Royalty)
    }
    set royalty(royalty: Royalty | undefined) {
        this.setForeignRecord('royalty', royalty)
    }
    get order(): Order | undefined {
        return this.getForeignRecord('order', Order)
    }
    set order(order: Order | undefined) {
        this.setForeignRecord('order', order)
    }
    get aPackage(): Package | undefined {
        return this.getForeignRecord('package', Package)
    }
    set aPackage(aPackage: Package | undefined) {
        this.setForeignRecord('package', aPackage)
    }
    get payment(): Payment | undefined {
        return this.getForeignRecord('payment', Payment)
    }
    set payment(payment: Payment | undefined) {
        this.setForeignRecord('payment', payment)
    }
    get charge(): Charge | undefined {
        return this.getForeignRecord('charge', Charge)
    }
    set charge(charge: Charge | undefined) {
        this.setForeignRecord('charge', charge)
    }
    get team(): Team | undefined {
        return this.getForeignRecord('team', Team)
    }
    set team(team: Team | undefined) {
        this.setForeignRecord('team', team)
    }
    get task(): Task | undefined {
        return this.getForeignRecord('task', Task)
    }
    set task(task: Task | undefined) {
        this.setForeignRecord('task', task)
    }
    get gift(): Gift | undefined {
        return this.getForeignRecord('gift', Gift)
    }
    set gift(gift: Gift | undefined) {
        this.setForeignRecord('gift', gift)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get record(): PSRecord | undefined {
        return this.getForeignRecord('record', PSRecord)
    }
    set record(record: PSRecord | undefined) {
        this.setForeignRecord('record', record)
    }
    get lesson(): Lesson | undefined {
        return this.getForeignRecord('lesson', Lesson)
    }
    set lesson(lesson: Lesson | undefined) {
        this.setForeignRecord('lesson', lesson)
    }
    get signature(): Signature | undefined {
        return this.getForeignRecord('signature', Signature)
    }
    set signature(signature: Signature | undefined) {
        this.setForeignRecord('signature', signature)
    }
    get conference(): Conference | undefined {
        return this.getForeignRecord('conference', Conference)
    }
    set conference(conference: Conference | undefined) {
        this.setForeignRecord('conference', conference)
    }
    get subscription(): Subscription | undefined {
        return this.getForeignRecord('subscription', Subscription)
    }
    set subscription(subscription: Subscription | undefined) {
        this.setForeignRecord('subscription', subscription)
    }
    get review(): Review | undefined {
        return this.getForeignRecord('review', Review)
    }
    set review(review: Review | undefined) {
        this.setForeignRecord('review', review)
    }
    get change(): Change | undefined {
        return this.getForeignRecord('change', Change)
    }
    set change(change: Change | undefined) {
        this.setForeignRecord('change', change)
    }
    get contact(): User | undefined {
        return this.getForeignRecord('contact', User)
    }
    set contact(contact: User | undefined) {
        this.setForeignRecord('contact', contact)
    }
    get template(): Template | undefined {
        return this.getForeignRecord('template', Template)
    }
    set template(template: Template | undefined) {
        this.setForeignRecord('template', template)
    }
}
